/**
 * Wire schemas for module 9 (owner extras): the owner's project home, the
 * timeline, the photo gallery, closeout, change requests, selections, the risk
 * register, warranties and warranty claims, ratings, companies on one's
 * projects and notification preferences.
 *
 * Synced to the device: warranty claims (raised offline; editable narrative
 * merged per field). Everything else is online. Money is
 * `{amount_minor, currency}`. Optional fields default so a newer server never
 * breaks an older client.
 */
import { z } from 'zod';

import { moneySchema } from './commercial-types';

const text = z.string().default('');
const date = z.string().nullable().default(null);
const nullableId = z.string().nullable().default(null);
const count = z.coerce.number().catch(0).default(0);
const bool = z.boolean().catch(false).default(false);
const money = moneySchema.nullable().catch(null).default(null);
const person = z
  .object({ id: z.string(), name: z.string().default('') })
  .nullable()
  .catch(null)
  .default(null);
const partyRef = z
  .object({ id: z.string(), party_role: z.string().default(''), name: z.string().default('') })
  .nullable()
  .catch(null)
  .default(null);

/** The actions the server says this person may take now (absent keys mean no). */
function can<const K extends string>(...keys: K[]): z.ZodType<Record<K, boolean>> {
  const pick = (raw: unknown) =>
    Object.fromEntries(
      keys.map((k) => [
        k,
        !!raw && typeof raw === 'object' && (raw as Record<string, unknown>)[k] === true,
      ]),
    );
  return z.unknown().transform(pick) as unknown as z.ZodType<Record<K, boolean>>;
}

// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------

export const CHANGE_REASONS = [
  'owner_preference',
  'budget',
  'design_issue',
  'regulatory',
  'other',
] as const;
export const SELECTION_CATEGORIES = [
  'finishes',
  'fixtures',
  'fittings',
  'appliances',
  'external',
  'other',
] as const;
export const RISK_CATEGORIES = [
  'design',
  'commercial',
  'programme',
  'site',
  'safety',
  'quality',
  'supply',
  'regulatory',
  'other',
] as const;
export const WARRANTY_CATEGORIES = [
  'structure',
  'roofing',
  'waterproofing',
  'plumbing',
  'electrical',
  'mechanical',
  'finishes',
  'equipment',
  'other',
] as const;
export const RATING_STAGES = ['milestone', 'practical_completion', 'closeout'] as const;
export const NOTIFICATION_CATEGORIES = [
  'approvals',
  'payments',
  'progress',
  'changes',
  'quality_safety',
  'documents',
  'closeout',
  'other',
] as const;

// ---------------------------------------------------------------------------
// Change requests and selections
// ---------------------------------------------------------------------------

export const changeRequestSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  title: text,
  description: text,
  reason: z.string().default('other'),
  location: text,
  zone_id: nullableId,
  desired_by: date,
  source: z.string().default('request'),
  selection_id: nullableId,
  status: z.string().default('submitted'),
  raised_by: person,
  raised_by_party: partyRef,
  raised_at: date,
  cost: money,
  time_days: z.number().nullable().catch(null).default(null),
  pricing_note: text,
  priced_by: person,
  priced_at: date,
  decided_by: person,
  decided_at: date,
  decision_note: text,
  variation: z
    .object({ id: z.string(), reference: text, status: text })
    .nullable()
    .catch(null)
    .default(null),
  can: can('edit', 'withdraw', 'price', 'decide'),
});
export type ChangeRequest = z.infer<typeof changeRequestSchema>;

export const selectionOptionSchema = z.object({
  id: z.string(),
  label: text,
  supplier: text,
  product: text,
  rate: money,
  total: money,
  difference: money,
  over_allowance: bool,
  spec_url: text,
  lead_time_days: z.number().nullable().catch(null).default(null),
  note: text,
});
export type SelectionOption = z.infer<typeof selectionOptionSchema>;

export const selectionSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  title: text,
  category: z.string().default('finishes'),
  location: text,
  zone_id: nullableId,
  stage_id: nullableId,
  stage_name: z.string().nullable().default(null),
  stage_start: date,
  description: text,
  quantity: z.string().default('1'),
  unit: text,
  allowance_rate: money,
  allowance: money,
  due_date: date,
  overdue: bool,
  late_for_programme: bool,
  status: z.string().default('draft'),
  prepared_by: person,
  prepared_by_party: partyRef,
  sent_at: date,
  options: z.array(selectionOptionSchema).catch([]).default([]),
  chosen_option_id: nullableId,
  chosen_by: person,
  chosen_at: date,
  choice_note: text,
  change_request: z
    .object({ id: z.string(), reference: text, status: text })
    .nullable()
    .catch(null)
    .default(null),
  can: can('edit', 'send', 'cancel', 'choose'),
});
export type Selection = z.infer<typeof selectionSchema>;

// ---------------------------------------------------------------------------
// Risks
// ---------------------------------------------------------------------------

export const riskSchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  title: text,
  description: text,
  category: z.string().default('other'),
  likelihood: count,
  impact: count,
  score: count,
  level: z.string().default('low'),
  owner_party: partyRef,
  owner_name: text,
  mitigation: text,
  status: z.string().default('open'),
  review_date: date,
  review_due: bool,
  visibility: z.string().default('shared'),
  side: text,
  raised_by: person,
  raised_by_party: partyRef,
  source_key: text,
  closed_at: date,
  closing_note: text,
  created_at: date,
  can: can('edit', 'set_visibility'),
});
export type Risk = z.infer<typeof riskSchema>;

export const riskSuggestionSchema = z.object({
  key: z.string(),
  category: z.string().default('other'),
  title: text,
  detail: text,
  likelihood: count,
  impact: count,
  score: count,
  level: z.string().default('low'),
  link: text,
  in_register: bool,
});
export type RiskSuggestion = z.infer<typeof riskSuggestionSchema>;

// ---------------------------------------------------------------------------
// Warranties and claims
// ---------------------------------------------------------------------------

export const warrantySchema = z.object({
  id: z.string(),
  number: count,
  reference: text,
  title: text,
  category: z.string().default('other'),
  supplier: text,
  party: partyRef,
  start_date: date,
  period_months: count,
  expires_on: date,
  state: z.string().default('pending'),
  days_left: z.number().nullable().catch(null).default(null),
  document_id: nullableId,
  document_title: z.string().nullable().default(null),
  reference_no: text,
  notes: text,
  can: can('edit'),
});
export type Warranty = z.infer<typeof warrantySchema>;

/** Editable offline (the narrative and what it relates to). */
export const WARRANTY_CLAIM_EDITABLE = [
  'title',
  'description',
  'location',
  'warranty_id',
  'noticed_on',
] as const;

/** A warranty claim as it syncs (and as the online register returns it, with `can`). */
export const warrantyClaimSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  number: count,
  reference: text,
  warranty_id: nullableId,
  warranty_title: z.string().nullable().default(null),
  title: text,
  description: text,
  location: text,
  noticed_on: date,
  status: z.string().default('open'),
  raised_by_id: nullableId,
  raised_by_name: text,
  raised_by_party_id: text,
  assigned_party_id: nullableId,
  assigned_company: z.string().nullable().default(null),
  assigned_at: date,
  respond_by: date,
  response: text,
  outcome: text,
  responded_at: date,
  closed_at: date,
  closing_note: text,
  created_at: date,
  version: z.number().nullable().catch(null).default(null),
  updated_at: date,
});
export type WarrantyClaim = z.infer<typeof warrantyClaimSchema>;

export const claimOnlineSchema = warrantyClaimSchema.extend({
  can: can('edit', 'assign', 'respond', 'close', 'reopen'),
});
export type ClaimOnline = z.infer<typeof claimOnlineSchema>;

export const partySchema = z.object({ id: z.string(), party_role: text, name: text });
export type Party = z.infer<typeof partySchema>;

// ---------------------------------------------------------------------------
// Ratings and companies
// ---------------------------------------------------------------------------

export const ratingSchema = z.object({
  id: z.string(),
  party_id: z.string(),
  company: text,
  party_role: text,
  stage: z.string().default('milestone'),
  label: text,
  quality: count,
  programme: count,
  communication: count,
  value: count,
  average: z.number().catch(0).default(0),
  comment: text,
  rated_by: person,
  rated_on: date,
});
export type Rating = z.infer<typeof ratingSchema>;

export const ratableSchema = z.object({
  party_id: z.string(),
  company: text,
  party_role: text,
  ratings: count,
  average: z.number().nullable().catch(null).default(null),
});
export type Ratable = z.infer<typeof ratableSchema>;

export const companySchema = z.object({
  organization_id: z.string(),
  name: text,
  kind: text,
  verification_status: text,
  roles: z.array(z.string()).catch([]).default([]),
  projects: z
    .array(z.object({ id: z.string(), name: text, party_id: text, role: text }))
    .catch([])
    .default([]),
  ratings: count,
  average: z.number().nullable().catch(null).default(null),
  /** An individual owner (name is "<Name> (owner)"): a person, never a company. */
  individual: z.boolean().catch(false).default(false),
});
export type Company = z.infer<typeof companySchema>;

// ---------------------------------------------------------------------------
// Read models
// ---------------------------------------------------------------------------

const inboxItemSchema = z
  .object({
    id: z.string(),
    kind: text,
    title: text,
    reference: text,
    amount: money,
    link: z.record(z.string(), z.unknown()).catch({}).default({}),
  })
  .passthrough();

export const ownerHomeSchema = z.object({
  project: z
    .object({
      id: z.string(),
      name: text,
      code: text,
      status: text,
      currency: z.string().default('KES'),
      target_completion_date: date,
      party_role: text,
      role: text,
    })
    .passthrough(),
  today: date,
  progress: z.object({
    verified_percent: z.coerce.number().catch(0).default(0),
    stages: z
      .array(
        z.object({
          id: z.string(),
          name: text,
          status: text,
          verified_percent: z.coerce.number().catch(0).default(0),
          planned_start: date,
          planned_end: date,
          behind: bool,
        }),
      )
      .catch([])
      .default([]),
  }),
  programme: z.object({
    baseline: z
      .object({
        name: text,
        finish_slip_days: z.number().nullable().catch(null).default(null),
        worst_slip_days: z.number().nullable().catch(null).default(null),
        stages_late: count,
      })
      .passthrough()
      .nullable()
      .catch(null)
      .default(null),
    stages_behind: count,
    days_remaining: z.number().nullable().catch(null).default(null),
  }),
  upcoming_milestones: z
    .array(z.object({ id: z.string(), name: text, due_date: date, late: bool }))
    .catch([])
    .default([]),
  money: z
    .object({
      contract_sum: money,
      approved_variations: money,
      pending_variations: money,
      revised_contract_sum: money,
      gross_certified: money,
      certified_payable: money,
      paid: money,
      outstanding: money,
      overdue: money,
      retention_held: money,
      forecast_final_account: money,
      certified_percent: z.coerce.number().catch(0).default(0),
    })
    .nullable()
    .catch(null)
    .default(null),
  decisions: z
    .object({
      count,
      by_kind: z.record(z.string(), z.number()).catch({}).default({}),
      items: z.array(inboxItemSchema).catch([]).default([]),
    })
    .nullable()
    .catch(null)
    .default(null),
  changes: z
    .object({
      requests_open: count,
      requests_to_decide: count,
      selections_open: count,
      selections_overdue: count,
      selections_due: z
        .array(z.object({ id: z.string(), reference: text, title: text, due_date: date }))
        .catch([])
        .default([]),
    })
    .nullable()
    .catch(null)
    .default(null),
  risks: z
    .object({
      open: count,
      high: count,
      suggestions: count,
      top: z
        .array(
          z.object({ id: z.string(), reference: text, title: text, score: count, level: text }),
        )
        .catch([])
        .default([]),
    })
    .nullable()
    .catch(null)
    .default(null),
  closeout: z
    .object({
      practical_completion: z
        .object({ status: text, completion_date: date, dlp_end: date })
        .nullable()
        .catch(null)
        .default(null),
      claims_open: count,
      warranties: count,
      warranties_expiring: count,
    })
    .nullable()
    .catch(null)
    .default(null),
});
export type OwnerHome = z.infer<typeof ownerHomeSchema>;

export const timelineEventSchema = z.object({
  at: z.string(),
  kind: text,
  title: text,
  detail: text,
  link: text,
  amount: money,
  percent: z.string().nullable().catch(null).default(null),
  revision: z.string().nullable().catch(null).default(null),
  option: z.string().nullable().catch(null).default(null),
});
export type TimelineEvent = z.infer<typeof timelineEventSchema>;
export const timelineSchema = z.object({
  events: z.array(timelineEventSchema).catch([]).default([]),
  next_before: date,
});
export type Timeline = z.infer<typeof timelineSchema>;

export const galleryPhotoSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  entity_type: text,
  entity_id: text,
  caption: text,
  has_derivatives: bool,
  status: text,
  taken_at: z.string(),
  date: z.string(),
  stage: z.string().nullable().default(null),
  zone: z.string().nullable().default(null),
  verified: bool,
});
export type GalleryPhoto = z.infer<typeof galleryPhotoSchema>;
export const gallerySchema = z.object({
  photos: z.array(galleryPhotoSchema).catch([]).default([]),
  next_before: date,
});
export type Gallery = z.infer<typeof gallerySchema>;

export const closeoutSchema = z.object({
  practical_completion: z
    .object({
      id: z.string(),
      number: count,
      status: text,
      scope: text,
      completion_date: date,
      dlp_months: z.number().nullable().catch(null).default(null),
      dlp_end: date,
      in_dlp: bool,
    })
    .nullable()
    .catch(null)
    .default(null),
  snags: z
    .object({ open: count, by_status: z.record(z.string(), z.number()).catch({}).default({}) })
    .nullable()
    .catch(null)
    .default(null),
  documents: z
    .record(z.string(), z.record(z.string(), z.number()))
    .nullable()
    .catch(null)
    .default(null),
  warranties: z
    .object({ count, by_state: z.record(z.string(), z.number()).catch({}).default({}) })
    .nullable()
    .catch(null)
    .default(null),
  claims: z.record(z.string(), z.number()).nullable().catch(null).default(null),
  commercial: z
    .object({
      retention: z
        .object({ held: money, released: money, retention_percent: text })
        .passthrough()
        .catch({ held: null, released: null, retention_percent: '' }),
      release_due: z
        .object({
          stage: z.string().nullable().default(null),
          released_stages: z.array(z.string()).catch([]).default([]),
        })
        .passthrough()
        .nullable()
        .catch(null)
        .default(null),
      final_account: z
        .object({ status: text, final_value: money, balance: money })
        .nullable()
        .catch(null)
        .default(null),
    })
    .nullable()
    .catch(null)
    .default(null),
  handover: z
    .object({ status: z.string().nullable().default(null), link: text })
    .nullable()
    .catch(null)
    .default(null),
  ratings: z.array(ratableSchema).nullable().catch(null).default(null),
});
export type Closeout = z.infer<typeof closeoutSchema>;

export const preferenceSchema = z.object({
  category: z.string(),
  in_app: bool,
  email: bool,
  sms: bool,
});
export type Preference = z.infer<typeof preferenceSchema>;
